import { useState, useEffect } from 'react'
import Head from 'next/head'
import Link from 'next/link'
import { useUserId } from '@nhost/nextjs'
import axios from 'axios'
import Image from 'next/legacy/image'
import { isIOS, isChrome, isEdge, isFirefox, isOpera } from 'react-device-detect'
import { ChevronDoubleRightIcon } from '@heroicons/react/outline'
import { Tooltip } from 'react-tooltip'

import { useInventoryByUserIdQuery, usePfpBoughtByUserAndPaymentQuery, useUserMetadataQuery } from 'generated/graphql'

import useViewport from 'contexts/viewport'
import ImageMasthead from 'components/image-masthead'
import SwitchProfile from 'components/switch-profile'
import { LoadingSpinner } from 'components/delayed'
import { Text, Button } from 'components/atomic'
import styles from './index.module.scss'
import Connections from './connections'
import { founderBlurDataURL } from 'constants/blurDataURL'
import EmailVerified from './email-verified'
import dayjs from 'dayjs'
import EditGamerTag from './edit-gamename'
import tooltipStyles from 'containers/tower-key/purchase/index.module.scss'
import Faq from 'containers/claws-nft/faq'

export type PaymentMethod = 'CC' | 'coinbase'
export type Payment = {
  method: PaymentMethod
  status: string
}

const GeneralProfilePage = () => {
  const localhostOrNil = window.location.origin.indexOf('://localhost') > -1 ? 'https://vsm-nft-fe.vercel.app' : ''
  const userId = useUserId()
  const { width: screenWidth, isMobile } = useViewport()

  const [nftsFounder, setNftsFounder] = useState<any>()
  const [walletAddr, setWalletAddr] = useState('')
  const [clawsNftCount, setClawsNftCount] = useState(0)
  const [paidProducts, setPaidProducts] = useState<any>([])

  const [loadingStardustWallet, setLoadingStardustWallet] = useState(false)
  const [loadingOthers, setLoadingOthers] = useState(true)

  const [email, setEmail] = useState('')
  const [emailVerifed, setEmailVerifed] = useState(false)

  const [canPlayInGame, setCanPlayInGame] = useState(false)
  const [gamerTag, setGamerTag] = useState('...')
  const [defaultGamerTag, setDefaultGamerTag] = useState('')
  const [isGameNameSettable, setIsGameNameSettable] = useState(false)
  const [gameNameSettableInMonths, setGameNameSettableInMonths] = useState(0)
  const [isShowEditGamerTag, setIsShowEditGamerTag] = useState(false)

  const { loading: userMetadataLoading, data } = useUserMetadataQuery()
  const nftsFounderQuery = usePfpBoughtByUserAndPaymentQuery({
    variables: {
      userId,
    },
  })
  const canPlayStatus = data?.users_metadata[0]?.early_access_passes[0]?.flags?.dk
    ? 'Game Access Claw'
    : 'Tower Key Claw'
  const hasGameAccess = !!(
    data?.users_metadata[0]?.nfts_founders[0]?.name || data?.users_metadata[0]?.early_access_passes[0]?.call_sign
  )

  const {
    loading: useInventoryByUserIdLoading,
    data: inventoryData,
    refetch: refetchInventoryData,
  } = useInventoryByUserIdQuery({
    variables: {
      userId,
    },
  })

  useEffect(() => {
    if (!nftsFounderQuery.loading) {
      const bought = nftsFounderQuery.data?.nfts_founder?.[0]

      if (bought) {
        setNftsFounder(bought)
      }
    }
  }, [nftsFounderQuery])

  useEffect(() => {
    const response = data?.users_metadata[0]

    if (response) {
      const currentUser = response?.user
      if (currentUser) {
        setEmail(currentUser.email)
        setEmailVerifed(currentUser.emailVerified)
      }
    }
  }, [data])

  useEffect(() => {
    const response = data?.users_metadata[0]
    const nftCount = data?.clawNfts?.length

    if (response) {
      // Set email
      setEmail(response.user?.email)

      // Set gamer tag
      setCanPlayInGame(
        nftsFounder?.name ||
          response.early_access_passes[0]?.call_sign ||
          dayjs().isBefore(dayjs(response.user?.guest_pass?.expires_at)),
      )
      setDefaultGamerTag(
        nftsFounder?.name || response.early_access_passes[0]?.call_sign || response.user?.guest_pass?.call_sign,
      )
      setGamerTag(
        response.game_name ||
          nftsFounder?.name ||
          response.early_access_passes[0]?.call_sign ||
          response.user?.guest_pass?.call_sign,
      )
      if (response.game_name_last_set) {
        setIsGameNameSettable(dayjs().diff(dayjs(response.game_name_last_set), 'month', true) >= 3)
        setGameNameSettableInMonths(3 - +dayjs().diff(dayjs(response.game_name_last_set), 'month', true).toFixed(2))
      } else {
        setIsGameNameSettable(true)
      }

      const fetchWalletStardust = async () => {
        let urlParam = `userId=${response.id}`
        if (response.stardust_playerId && nftCount) urlParam = `playerId=${response.stardust_playerId}`
        try {
          setLoadingStardustWallet(true)
          const res = await axios.get(`/api/getCustodialWallet?${urlParam}`)
          const wallet = res.data?.wallet || '' // ex. 0x261eC38c839580ee29baEa690243Bc6EEC0D302d
          if (wallet.length === 42) {
            setWalletAddr(wallet)
          }
        } catch (err) {
          console.error('[getCustodialWallet]', err)
        } finally {
          setLoadingStardustWallet(false)
        }
      }

      if (response.wallet_stardust) setWalletAddr(response.wallet_stardust)
      else fetchWalletStardust()
    }
    if (nftCount) setClawsNftCount(nftCount)
    if (response || nftCount) setLoadingOthers(false)
  }, [data, nftsFounder])

  useEffect(() => {
    const response = inventoryData?.paid_products
    if (response && response.length > 0) {
      setPaidProducts(response)
    }
  }, [inventoryData])

  if (nftsFounderQuery.loading || userMetadataLoading || useInventoryByUserIdLoading || loadingOthers) {
    return (
      <ImageMasthead
        image={
          isMobile ? '/images/profile/access_pass_buy_prompt_mobile.png' : '/images/profile/access_pass_buy_prompt.png'
        }
        className="h-[750px] sm:h-[840px] sm:!max-h-[unset]"
      >
        <div className="relative h-[750px] pb-12 pt-24 sm:h-[840px] lg:pt-40">
          <LoadingSpinner isShown isFullScreen={false} />
        </div>
      </ImageMasthead>
    )
  }
  return (
    <div>
      <Head>
        <title>My Profile | Midnight Society</title>
      </Head>
      <ImageMasthead
        image={screenWidth < 428 ? '/images/profile/profile_bg_mobile.png' : '/images/profile/profile_bg.png'}
        className="!max-h-[540px]"
      >
        {/* Switch profile */}
        <SwitchProfile isFounderProfile={!!nftsFounder} />

        {/* User information */}
        <div className="relative">
          <div className="mx-auto max-w-[1440px] px-8 pt-2 lg:pt-12 xl:px-16">
            <div className="flex flex-col items-center lg:flex-row lg:items-stretch lg:justify-between">
              {/* User info */}
              <div className="mr-0 w-full lg:mr-8">
                <div className="mb-8 flex flex-col items-center lg:flex-row">
                  {/* Avatar */}
                  <div className="relative mb-11 mr-0 h-[200px] w-[200px] shrink-0 lg:mb-0 lg:mr-6 lg:h-[115px] lg:w-[115px]">
                    <Image
                      src={
                        nftsFounder
                          ? `${localhostOrNil}/founders_pass_art/${nftsFounder['onchain_tokenId']}.jpg?thumb`
                          : '/images/profile/pfp.png'
                      }
                      layout="fill"
                      objectFit="cover"
                      alt="Access Pass Holder"
                      placeholder="blur"
                      blurDataURL={founderBlurDataURL}
                      unoptimized={Boolean(nftsFounder)}
                    />
                  </div>
                  {/* User infomation */}
                  <div className="flex flex-col items-center lg:block">
                    {/* Display name */}
                    <Text
                      className={'mb-3 text-center tracking-tighter lg:text-left' + (nftsFounder ? '' : ' break-all')}
                      variant="h4"
                      tag="h4"
                    >
                      {nftsFounder
                        ? nftsFounder.name
                        : canPlayInGame
                          ? data?.users_metadata[0]?.early_access_passes[0]?.call_sign
                          : (data?.users_metadata[0]?.user.guest_pass?.call_sign ??
                            data?.users_metadata[0]?.user.displayName)}
                    </Text>
                    {/* Gamertag */}
                    {canPlayInGame && (
                      <div className="flex items-center">
                        <Text
                          variant="p2"
                          tag="p"
                          className="text-center font-semibold tracking-wide text-white/70 lg:text-left"
                        >
                          Gamertag: {gamerTag}
                        </Text>
                        {isGameNameSettable && (
                          <div
                            onClick={(e) => setIsShowEditGamerTag(!isShowEditGamerTag)}
                            className="relative ml-2 h-[25px] w-[25px] cursor-pointer"
                          >
                            <Image src="/images/profile/btn-edit-gamertag.png" layout="fill" alt="Edit gamertag" />
                          </div>
                        )}
                        {/* [RESET] button to set game_name to null, so default gamertag is used. If no game_name param specified to /api/update-game-name. Tooltip on hover to explain how interactions work with game_name_last_set */}
                        {gamerTag !== defaultGamerTag && (
                          <div className={tooltipStyles.eap_note}>
                            <div
                              id="reset-tooltip"
                              className="ml-2 cursor-pointer"
                              onClick={async (e) => {
                                try {
                                  const isConfirmed = confirm(
                                    `Do you want to reset your gamertag to ${defaultGamerTag}? You will have a 3 months cooldown if you edit your gamertag again after.`,
                                  )
                                  if (isConfirmed) {
                                    setGamerTag('...')
                                    await axios.get(`/api/update-game-name`)
                                    setGamerTag(defaultGamerTag)
                                  }
                                } catch (error) {
                                  console.log('Gamertag reset error:', error)
                                }
                              }}
                            >
                              <Text
                                variant="body1"
                                className="rounded-full bg-black/30 px-1.5 text-[11px] font-semibold uppercase text-primary"
                              >
                                Reset
                              </Text>
                            </div>
                            <Tooltip
                              anchorSelect="#reset-tooltip"
                              html={`This resets your gamertag to your default callsign: <b>${defaultGamerTag}</b><br /><br /><b><u>NOTE</u></b> that ${
                                gameNameSettableInMonths > 0
                                  ? `you can edit your gamertag again in ${gameNameSettableInMonths} months`
                                  : `if you want to edit your gamertag after this reset, including revert to <b>${gamerTag}</b>, 3 months timeout will re-apply`
                              }`}
                              place="bottom"
                              className={tooltipStyles.profile_tooltip}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {/* Status */}
                    <Text
                      variant="p2"
                      tag="p"
                      className="text-center font-semibold tracking-wide text-white/70 lg:text-left"
                    >
                      Status: {nftsFounder ? 'Variant' : canPlayInGame ? canPlayStatus : 'Claw'}
                    </Text>
                    {/* Email */}
                    <Text
                      variant="p2"
                      tag="p"
                      className="text-center font-semibold tracking-wide text-white/70 lg:text-left"
                    >
                      Email: {emailVerifed ? email : 'Unverified'}
                    </Text>
                  </div>
                </div>
                {isShowEditGamerTag && (
                  <div className="hidden lg:block">
                    <EditGamerTag isSmallScreen gamerTag={gamerTag} setGamerTag={setGamerTag} />
                  </div>
                )}
                {!emailVerifed && (
                  <div className="hidden lg:block">
                    <EmailVerified isSmallScreen userEmail={data?.users_metadata[0]?.user.email} />
                  </div>
                )}
              </div>
              {/* Download button */}
              {canPlayInGame && (
                <Button variant="primary" href="/download" className="mmb-12 d:w-full">
                  <div className="flex w-full items-center justify-between">
                    <div className="mr-2 h-[40px] w-[32px]">
                      <Image src="/images/profile/Logo_Deadrop_black.svg" alt="download icon" width={32} height={40} />
                    </div>
                    <span className="flex-1 font-bold">Download Deadrop</span>
                  </div>
                </Button>
              )}
            </div>
          </div>
        </div>
      </ImageMasthead>

      {isShowEditGamerTag && (
        <div className="w-full lg:hidden">
          <EditGamerTag isSmallScreen gamerTag={gamerTag} setGamerTag={setGamerTag} />
        </div>
      )}
      {!emailVerifed && (
        <div className="w-full lg:hidden">
          <EmailVerified isSmallScreen userEmail={data?.users_metadata[0]?.user.email} />
        </div>
      )}

      {/* Connections tabs */}
      <div className="block">
        <Connections
          paidProducts={paidProducts}
          hasGameAccess={hasGameAccess}
          clawsNftCount={clawsNftCount}
          loadingStardustWallet={loadingStardustWallet}
          isSmallScreen
          refetchInventoryData={refetchInventoryData}
        />
      </div>

      <div className="mx-4 my-16 md:mb-4">
        <Link href="/faq" passHref>
          <Text
            className="flex justify-center self-center text-center align-middle tracking-tighter text-secondary hover:underline"
            variant="h4"
            tag="h4"
          >
            Frequently Asked Questions
            <ChevronDoubleRightIcon className="ml-2 h-8 w-8 self-center md:ml-4" />
          </Text>
        </Link>
      </div>
    </div>
  )
}

export default GeneralProfilePage
